import LegalPageShell, { H2, P, UL } from "../../components/LegalPageShell";

export default function PrivacyPolicy() {
  return (
    <LegalPageShell
      eyebrow="LEGAL · PRIVACY POLICY"
      title="Privacy Policy"
      lastUpdated="February 2026"
      testId="privacy-page"
    >
      <P>
        Wanted ("we," "us," "our") respects your privacy. This Privacy Policy describes how we collect, use,
        share, and protect your personal information when you use the Wanted website and services (the
        "Service"). It also explains the choices you have about your information.
      </P>

      <H2>1. Information We Collect</H2>
      <P>We collect the following categories of information:</P>
      <UL>
        <li><strong className="text-white">Account information</strong> — your name, email address, password (stored hashed), and authentication provider.</li>
        <li><strong className="text-white">Profile information</strong> — age, location, occupation, bio, interests, gender, who you are open to meeting, and photos you upload.</li>
        <li><strong className="text-white">Verification</strong> — a selfie image you submit for identity verification, if requested.</li>
        <li><strong className="text-white">Messages and connections</strong> — the messages you exchange with matches and the matches you create.</li>
        <li><strong className="text-white">Payment information</strong> — handled by our payment processor, Stripe. We never see or store your full card number.</li>
        <li><strong className="text-white">Usage information</strong> — log files, IP address, device type, browser, pages visited, and timestamps.</li>
      </UL>

      <H2>2. How We Use Your Information</H2>
      <UL>
        <li>Create and operate your account, profile, matching, and messaging.</li>
        <li>Verify identity and keep the community safe.</li>
        <li>Process payments and manage memberships.</li>
        <li>Communicate with you (e.g., service updates, password resets, support).</li>
        <li>Improve and secure the Service, including detecting fraud and abuse.</li>
        <li>Comply with legal obligations.</li>
      </UL>

      <H2>3. Sharing Your Information</H2>
      <P>
        We do not sell your personal information. We share it only:
      </P>
      <UL>
        <li>With other members, to the extent you choose to display it on your profile or in conversations.</li>
        <li>With service providers that operate parts of the Service for us (for example, cloud hosting, file storage, email delivery, and payment processing) under contractual confidentiality and data-protection obligations.</li>
        <li>To comply with law, valid legal requests, or to protect the rights, property, or safety of Wanted, our members, or the public.</li>
        <li>In connection with a merger, acquisition, or sale of all or a portion of our business, with appropriate notice to you.</li>
      </UL>

      <H2>4. Your Choices</H2>
      <UL>
        <li><strong className="text-white">Access and update.</strong> You can view and edit most of your profile information at any time from inside the app.</li>
        <li><strong className="text-white">Delete your account.</strong> You can delete your account by contacting us at hello@joinwanted.com. We will remove your profile from public view and delete or anonymize your personal data unless we are required by law to retain it.</li>
        <li><strong className="text-white">Communication preferences.</strong> You can unsubscribe from non-essential emails by using the unsubscribe link in any email.</li>
        <li><strong className="text-white">Cookies.</strong> Most browsers let you control cookies. We use cookies for essential functionality such as keeping you signed in.</li>
      </UL>

      <H2>5. Your Rights (GDPR, CCPA, and similar laws)</H2>
      <P>
        Depending on where you live, you may have the right to access, correct, delete, restrict, or
        port your personal information, or to object to certain processing. To exercise these rights,
        email us at <a href="mailto:hello@joinwanted.com" className="text-gold hover:text-gold-soft">hello@joinwanted.com</a>.
        We will respond within the timeframes required by applicable law.
      </P>

      <H2>6. Data Retention</H2>
      <P>
        We keep your information for as long as your account is active and as needed to provide the Service.
        After deletion or account closure, we may retain limited information for legitimate purposes such as
        legal compliance, fraud prevention, and dispute resolution.
      </P>

      <H2>7. Security</H2>
      <P>
        We use industry-standard technical and organizational measures to protect your information,
        including password hashing, encrypted transport (HTTPS), and access controls. No system is perfectly
        secure, however. If you believe your account has been compromised, contact us immediately.
      </P>

      <H2>8. Children</H2>
      <P>
        Wanted is only for adults aged 18 and older. We do not knowingly collect personal information from
        anyone under 18. If you believe a minor has provided us with personal information, please contact us
        and we will take prompt action.
      </P>

      <H2>9. International Transfers</H2>
      <P>
        Wanted may process information in countries other than your own. Where required, we use
        appropriate safeguards to protect your information during these transfers.
      </P>

      <H2>10. Changes to This Policy</H2>
      <P>
        We may update this Privacy Policy from time to time. If changes are material, we will provide notice
        on the Service or by email. Continued use of the Service after changes take effect constitutes your
        acceptance of the updated Policy.
      </P>

      <H2>11. Contact</H2>
      <P>
        For any privacy questions or requests, please email{" "}
        <a href="mailto:hello@joinwanted.com" className="text-gold hover:text-gold-soft">
          hello@joinwanted.com
        </a>.
      </P>
    </LegalPageShell>
  );
}
